/**
 * Berechnung der Arbeitstage zwischen zwei Daten.
 * Ausgenommen: Samstage, Sonntage und gesetzliche Feiertage in NRW.
 */

/**
 * Berechnet das Datum des Ostersonntags fuer ein gegebenes Jahr
 * (Anonymer Gregorianischer Algorithmus / Meeus-Jones-Butcher)
 */
function osterSonntag(jahr: number): Date {
  const a = jahr % 19;
  const b = Math.floor(jahr / 100);
  const c = jahr % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monat = Math.floor((h + l - 7 * m + 114) / 31);
  const tag = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(jahr, monat - 1, tag);
}

/**
 * Gibt ein neues Date zurueck, das um `tage` Tage verschoben ist.
 */
function addTage(datum: Date, tage: number): Date {
  const result = new Date(datum);
  result.setDate(result.getDate() + tage);
  return result;
}

/**
 * Formatiert ein Datum als "YYYY-MM-DD" String fuer Vergleiche.
 */
function datumKey(datum: Date): string {
  const y = datum.getFullYear();
  const m = String(datum.getMonth() + 1).padStart(2, '0');
  const d = String(datum.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Gibt alle gesetzlichen Feiertage in NRW fuer ein bestimmtes Jahr zurueck.
 *
 * NRW-Feiertage:
 *  - Neujahr (1. Januar)
 *  - Karfreitag (Ostersonntag - 2)
 *  - Ostermontag (Ostersonntag + 1)
 *  - Tag der Arbeit (1. Mai)
 *  - Christi Himmelfahrt (Ostersonntag + 39)
 *  - Pfingstmontag (Ostersonntag + 50)
 *  - Fronleichnam (Ostersonntag + 60)
 *  - Tag der Deutschen Einheit (3. Oktober)
 *  - Allerheiligen (1. November)
 *  - 1. Weihnachtstag (25. Dezember)
 *  - 2. Weihnachtstag (26. Dezember)
 */
export function feiertageNRW(jahr: number): Map<string, string> {
  const ostern = osterSonntag(jahr);
  const feiertage = new Map<string, string>();

  // Feste Feiertage
  feiertage.set(datumKey(new Date(jahr, 0, 1)), 'Neujahr');
  feiertage.set(datumKey(new Date(jahr, 4, 1)), 'Tag der Arbeit');
  feiertage.set(datumKey(new Date(jahr, 9, 3)), 'Tag der Deutschen Einheit');
  feiertage.set(datumKey(new Date(jahr, 10, 1)), 'Allerheiligen');
  feiertage.set(datumKey(new Date(jahr, 11, 25)), '1. Weihnachtstag');
  feiertage.set(datumKey(new Date(jahr, 11, 26)), '2. Weihnachtstag');

  // Bewegliche Feiertage (abhaengig von Ostern)
  feiertage.set(datumKey(addTage(ostern, -2)), 'Karfreitag');
  feiertage.set(datumKey(addTage(ostern, 1)), 'Ostermontag');
  feiertage.set(datumKey(addTage(ostern, 39)), 'Christi Himmelfahrt');
  feiertage.set(datumKey(addTage(ostern, 50)), 'Pfingstmontag');
  feiertage.set(datumKey(addTage(ostern, 60)), 'Fronleichnam');

  return feiertage;
}

/**
 * Berechnet die Anzahl der Arbeitstage zwischen zwei Daten (inklusive Start und Ende).
 * Wochenenden (Samstag, Sonntag) und NRW-Feiertage werden nicht gezaehlt.
 *
 * @param vonStr - Startdatum als "YYYY-MM-DD"
 * @param bisStr - Enddatum als "YYYY-MM-DD"
 * @returns Anzahl der Arbeitstage, oder 0 wenn ungueltig
 */
export function berechneArbeitstage(vonStr: string, bisStr: string): number {
  if (!vonStr || !bisStr) return 0;

  const von = new Date(vonStr + 'T00:00:00');
  const bis = new Date(bisStr + 'T00:00:00');

  if (isNaN(von.getTime()) || isNaN(bis.getTime())) return 0;
  if (bis < von) return 0;

  // Feiertage fuer alle betroffenen Jahre sammeln
  const jahreSet = new Set<number>();
  for (let j = von.getFullYear(); j <= bis.getFullYear(); j++) {
    jahreSet.add(j);
  }
  const alleFeiertage = new Set<string>();
  jahreSet.forEach((j) => {
    const ft = feiertageNRW(j);
    ft.forEach((_, key) => alleFeiertage.add(key));
  });

  let arbeitstage = 0;
  const current = new Date(von);

  while (current <= bis) {
    const wochentag = current.getDay(); // 0=So, 6=Sa
    const key = datumKey(current);

    if (wochentag !== 0 && wochentag !== 6 && !alleFeiertage.has(key)) {
      arbeitstage++;
    }

    current.setDate(current.getDate() + 1);
  }

  return arbeitstage;
}

/**
 * Sammelt NRW-Feiertags-Keys fuer eine Spanne von Jahren (fuer Iteration ueber viele Monate).
 */
function feiertageKeysFuerJahre(jahrVon: number, jahrBis: number): Set<string> {
  const s = new Set<string>();
  for (let j = jahrVon; j <= jahrBis; j++) {
    feiertageNRW(j).forEach((_, key) => s.add(key));
  }
  return s;
}

/**
 * Enddatum so dass ab `von` (inkl.) genau `anzahlTage` Arbeitstage „verbraucht“ werden
 * (inkl. 0,5-Schritten: pro Kalendertag max. 1 Arbeitstag, Rest als Teil eines Tages).
 * Entspricht dem Rueckweg zur Zaehlweise in `berechneArbeitstage` (Mo–Fr, ohne NRW-Feiertage).
 *
 * @returns `YYYY-MM-DD` oder leerer String bei ungueltiger Eingabe / wenn kein Ende ermittelt werden kann
 */
export function endeNachArbeitstagen(vonStr: string, anzahlTage: number): string {
  if (!vonStr || !Number.isFinite(anzahlTage) || anzahlTage <= 0) return '';

  const von = new Date(vonStr + 'T00:00:00');
  if (isNaN(von.getTime())) return '';

  let remaining = anzahlTage;
  const current = new Date(von);
  const y0 = von.getFullYear();
  const alleFeiertage = feiertageKeysFuerJahre(y0, y0 + 3);

  const maxGuard = 800;
  for (let guard = 0; guard < maxGuard; guard++) {
    const wochentag = current.getDay();
    const key = datumKey(current);
    const istArbeitstag = wochentag !== 0 && wochentag !== 6 && !alleFeiertage.has(key);

    if (!istArbeitstag) {
      current.setDate(current.getDate() + 1);
      continue;
    }

    const take = Math.min(1, remaining);
    remaining -= take;
    if (remaining <= 1e-9) {
      return datumKey(current);
    }
    current.setDate(current.getDate() + 1);
  }

  return '';
}

/**
 * Gibt eine Liste der Feiertage zurueck, die in den Zeitraum fallen.
 * Nuetzlich fuer die Anzeige im UI.
 */
export function feiertageImZeitraum(
  vonStr: string,
  bisStr: string
): Array<{ datum: string; name: string }> {
  if (!vonStr || !bisStr) return [];

  const von = new Date(vonStr + 'T00:00:00');
  const bis = new Date(bisStr + 'T00:00:00');

  if (isNaN(von.getTime()) || isNaN(bis.getTime())) return [];
  if (bis < von) return [];

  const result: Array<{ datum: string; name: string }> = [];

  for (let j = von.getFullYear(); j <= bis.getFullYear(); j++) {
    const ft = feiertageNRW(j);
    ft.forEach((name, key) => {
      const d = new Date(key + 'T00:00:00');
      if (d >= von && d <= bis) {
        result.push({ datum: key, name });
      }
    });
  }

  result.sort((a, b) => a.datum.localeCompare(b.datum));
  return result;
}
